import React, { createContext, useContext, useRef, useState, useCallback } from "react";
import axios from "axios";
import { useLang } from "@/context/LangContext";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const SEP = "\u0000";
const ContentI18nContext = createContext(null);
export const useContentI18n = () => useContext(ContentI18nContext);

export function ContentI18nProvider({ children }) {
  const { lang } = useLang();
  const [cache, setCache] = useState({});
  const queueRef = useRef(new Set());
  const requestedRef = useRef(new Set());
  const timerRef = useRef(null);

  const needsTr = lang === "ta" || lang === "hi" || lang === "te" || lang === "bn";

  const flush = useCallback((curLang) => {
    const items = Array.from(queueRef.current);
    queueRef.current.clear();
    if (!items.length) return;
    axios.post(`${API}/i18n/translate`, { lang: curLang, texts: items }).then((r) => {
      setCache((prev) => {
        const next = { ...prev };
        for (const [src, dst] of Object.entries(r.data || {})) next[`${curLang}${SEP}${src}`] = dst;
        return next;
      });
    }).catch(() => {});
  }, []);

  const schedule = useCallback((en) => {
    const key = `${lang}${SEP}${en}`;
    if (requestedRef.current.has(key)) return;
    requestedRef.current.add(key);
    queueRef.current.add(en);
    if (timerRef.current) clearTimeout(timerRef.current);
    const curLang = lang;
    timerRef.current = setTimeout(() => flush(curLang), 60);
  }, [lang, flush]);

  const text = useCallback((str) => {
    if (!needsTr || !str) return str;
    const key = `${lang}${SEP}${str}`;
    if (cache[key] != null) return cache[key];
    schedule(str);
    return str;
  }, [needsTr, lang, cache, schedule]);

  const title = useCallback((obj) => {
    if (!obj) return "";
    if (lang === "si") return obj.title_si || obj.title_en || "";
    if (lang === "en") return obj.title_en || obj.title_si || "";
    return text(obj.title_en || obj.title_si || "");
  }, [lang, text]);

  return (
    <ContentI18nContext.Provider value={{ title, text, lang }}>
      {children}
    </ContentI18nContext.Provider>
  );
}
